import { PLAYGAMA } from '@/modules/adv/header-bidding/bidders-config'
import { AppNativeSDK } from '@/modules/native-app'
import {
    AdProviderTypes,
    type HeaderBiddingAdSettings,
    type PlatformType,
    type AppProviderSettings,
    type GoogleAdBannerSettings,
    type GoogleAdSettings,
    type GoogleInstreamSettings,
} from '@/types'

import { DEFAULT_AD_UNIT_TIMEOUT } from './constants'

type GoogleAdOverrides = Pick<GoogleAdSettings['options'], 'adUnitConfig'> &
    Partial<Omit<GoogleAdSettings['options'], 'adUnitConfig'>>

type GoogleBannerOverrides = Pick<GoogleAdBannerSettings['options'], 'adUnitConfig'> &
    Partial<Omit<GoogleAdBannerSettings['options'], 'adUnitConfig'>>

type GoogleInstreamOverrides = Pick<GoogleInstreamSettings['options'], 'adUnitConfig'> &
    Partial<Omit<GoogleInstreamSettings['options'], 'adUnitConfig' | 'name'>>

type AppNativeOverrides = Pick<AppProviderSettings['options'], 'adUnitConfig'> &
    Partial<Omit<AppProviderSettings['options'], 'adUnitConfig'>>

const APP_NATIVE_TIMEOUT = 6000
const INSTREAM_TIMEOUT = 5000

const GOOGLE_AD_PAGE_DOMAIN = 'playgama.com'
const GOOGLE_AD_PAGE_URL = 'https://playgama.com'

export const createGoogleAdSettings = (
    gamAccount: string,
    platform: PlatformType,
    overrides: GoogleAdOverrides,
): GoogleAdSettings => ({
    type: AdProviderTypes.GAM_H5,
    options: {
        platform,
        pageDomain: GOOGLE_AD_PAGE_DOMAIN,
        gamAccount,
        timeout: DEFAULT_AD_UNIT_TIMEOUT,
        ...overrides,
    },
})

export const createGoogleBannerSettings = (
    gamAccount: string,
    platform: PlatformType,
    overrides: GoogleBannerOverrides,
): GoogleAdBannerSettings => ({
    type: AdProviderTypes.GAM_BANNER_INTERSTITIAL,
    options: {
        platform,
        pageDomain: GOOGLE_AD_PAGE_DOMAIN,
        gamAccount,
        timeout: DEFAULT_AD_UNIT_TIMEOUT,
        ...overrides,
    },
})

export const createGoogleInstreamSettings = (
    gamAccount: string,
    platform: PlatformType,
    overrides: GoogleInstreamOverrides,
): GoogleInstreamSettings => ({
    type: AdProviderTypes.GAM_INSTREAM,
    options: {
        headerBidding: {
            bidders: [PLAYGAMA],
        },
        platform,
        pageURL: GOOGLE_AD_PAGE_URL,
        gamAccount,
        timeout: INSTREAM_TIMEOUT,
        useCurrentPageUrl: true,
        ...overrides,
    },
})

export const createAppNativeSettings = (
    appNativeSDK: AppNativeSDK | undefined,
    platform: PlatformType,
    overrides: AppNativeOverrides,
): AppProviderSettings => ({
    type: AdProviderTypes.APP,
    options: {
        appNativeSDK,
        timeout: APP_NATIVE_TIMEOUT,
        platform,
        ...overrides,
    },
})

export const createHeaderBiddingAdSettings = (
    platform: PlatformType,
    bidders: HeaderBiddingAdSettings['options']['bidders'],
): HeaderBiddingAdSettings => ({
    type: AdProviderTypes.PREBID_DIRECT,
    options: {
        timeout: DEFAULT_AD_UNIT_TIMEOUT,
        platform,
        bidders,
    },
})
